import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { ChannelConfigService } from './channel-config.service';
import { ChannelThemeService } from './channel-theme.service';
import { UManagerSlugService, ChannelData } from './umanager-slug.service';
import { UtmPreservationService } from './utm-preservation.service';

/**
 * Loads channel data from utm_source for the whole app — including auth routes
 * that never mount HeaderComponent / LayoutComponent.
 */
@Injectable({ providedIn: 'root' })
export class ChannelBootstrapService {
  private readonly destroy$ = new Subject<void>();
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly umanagerSlugService = inject(UManagerSlugService);
  private readonly channelConfigService = inject(ChannelConfigService);
  private readonly channelThemeService = inject(ChannelThemeService);
  private readonly utmPreservationService = inject(UtmPreservationService);

  private initialized = false;
  private lastResolvedUtm: string | null | undefined = undefined;
  private inFlight$: Observable<void> | null = null;

  initialize(): void {
    if (!isPlatformBrowser(this.platformId) || this.initialized) {
      return;
    }
    this.initialized = true;

    // Kick off immediately so cold loads on auth routes resolve before paint settles.
    this.ensureLoaded().subscribe();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const currentUtm = this.getCurrentUtmSource();
        if (currentUtm !== this.lastResolvedUtm) {
          this.ensureLoaded().subscribe();
        }
      });
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.initialized = false;
    this.inFlight$ = null;
    this.lastResolvedUtm = undefined;
  }

  /**
   * Resolves once channel data for the current utm_source has been applied
   * (or there is no utm / lookup finished without a channel).
   */
  ensureLoaded(): Observable<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(undefined);
    }

    const utmSource = this.getCurrentUtmSource();

    if (!utmSource) {
      if (this.lastResolvedUtm !== null) {
        this.applyNoChannel();
        this.lastResolvedUtm = null;
      }
      return of(undefined);
    }

    if (utmSource === this.lastResolvedUtm && !this.inFlight$) {
      return of(undefined);
    }

    if (this.inFlight$ && utmSource === this.lastResolvedUtm) {
      return this.inFlight$;
    }

    // Mark the UTM we're resolving so concurrent callers share the same in-flight.
    this.lastResolvedUtm = utmSource;

    this.inFlight$ = this.umanagerSlugService.checkSlugExists(utmSource).pipe(
      take(1),
      switchMap((response) => {
        if (!response?.exists) {
          this.applyNoChannel();
          return of(undefined);
        }
        return this.umanagerSlugService.getChannelBySlug(utmSource).pipe(
          take(1),
          tap((channelData) => this.applyChannel(channelData)),
          map(() => undefined)
        );
      }),
      catchError((error) => {
        console.error('[ChannelBootstrap] Failed to load channel:', error);
        this.applyNoChannel();
        return of(undefined);
      }),
      finalize(() => {
        this.inFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.inFlight$;
  }

  private applyChannel(channelData: ChannelData | null): void {
    if (channelData?.status && channelData.status !== 'ACTIVE') {
      this.utmPreservationService.disableUtmAndCleanUrl();
      this.applyNoChannel();
      return;
    }

    this.channelConfigService.setChannelData(channelData);
    const parsedColors = this.channelThemeService.parseChannelColors(
      channelData?.colors ?? null
    );
    if (parsedColors) {
      this.channelThemeService.applyChannelColors(parsedColors);
    } else {
      this.channelThemeService.resetToDefault();
    }
  }

  private applyNoChannel(): void {
    this.channelConfigService.setChannelData(null);
    this.channelThemeService.resetToDefault();
  }

  private getCurrentUtmSource(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get('utm_source');
    if (fromUrl) {
      return fromUrl;
    }
    return this.utmPreservationService.preservedUtmParams?.['utm_source'] || null;
  }
}
